import type { MouseEvent } from 'react';
import React, { useState } from 'react';
import {
    List,
    Popper,
    Fade,
    Box,
    makeStyles,
    IconButton,
    ClickAwayListener,
} from '@material-ui/core';
import type { PopperPlacementType } from '@material-ui/core/Popper';

import { IconName, Illustration } from '@klp/design/Illustrations';

import { RemoveUserDialog } from '../components/RemoveUserDialog';
import { ToggleAdmin } from '../components/ToggleAdmin';
import { ToggleSuperUser } from '../components/ToggleSuperUser';

interface PopperProps {
    fetchUsersForSelectedClient: () => void;
    handleUser: {
        isSuperUser: boolean;
        isAdmin: boolean;
        email: string;
        isAdminUserAllowed: boolean;
    };
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
}
const useStyles = makeStyles(() => ({
    popper: {
        background: 'white',
        border: '1px solid',
        borderRadius: 2,
    },
    redText: {
        color: 'red',
    },
    button: {
        border: '1px solid transparent',
        borderRadius: '50%',
        cursor: 'pointer',
        '&:hover': {
            border: '1px solid',
        },
    },
}));

export default function TransitionsPopper({
    handleUser,
    fetchUsersForSelectedClient,
    isOpen,
    onOpen,
    onClose,
}: PopperProps) {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [placement, setPlacement] = useState<PopperPlacementType>();

    const handlePopperClick =
        (newPlacement: PopperPlacementType) =>
        (event: MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
            if (isOpen) {
                onClose();
            } else {
                onOpen();
            }
            setPlacement(newPlacement);
            event.stopPropagation();
        };

    const handleActionSuccess = () => {
        fetchUsersForSelectedClient();
        onClose();
    };

    const handleClickAway = () => {
        // Don't close popper if a Material-UI dialog is open
        const dialogs = document.querySelectorAll('[role="dialog"]');
        if (dialogs.length === 0) {
            onClose();
        }
    };

    const canBeOpen = isOpen && Boolean(anchorEl);
    const id = canBeOpen ? 'transition-popper' : undefined;

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div>
                <IconButton
                    color="inherit"
                    aria-label="Open document view"
                    onClick={handlePopperClick('bottom-start')}
                    className={classes.button}
                >
                    <Illustration icon={IconName.OPTIONS} size="medium" />
                </IconButton>
                <Popper
                    id={id}
                    open={isOpen}
                    anchorEl={anchorEl}
                    placement={placement}
                    transition={true}
                >
                    {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={350}>
                            <Box className={classes.popper}>
                                <List>
                                    {handleUser.isAdminUserAllowed && (
                                        <ToggleAdmin
                                            email={handleUser.email}
                                            isAdmin={handleUser.isAdmin}
                                            onSuccess={handleActionSuccess}
                                        />
                                    )}
                                    <ToggleSuperUser
                                        email={handleUser.email}
                                        isSuperUser={handleUser.isSuperUser}
                                        onSuccess={handleActionSuccess}
                                    />
                                    <RemoveUserDialog
                                        email={handleUser.email}
                                        onSuccess={handleActionSuccess}
                                    />
                                </List>
                            </Box>
                        </Fade>
                    )}
                </Popper>
            </div>
        </ClickAwayListener>
    );
}
